import { useState } from 'react';
import { useCompetition, useCompetitionIdParam, useSetOpenForSignup } from '@/hooks/useCompetitions';
import {
  useAssignParticipant,
  useParticipantsForCompetition,
  useRemoveParticipant,
  useSetParticipantPaid,
} from '@/hooks/useCompetitionParticipants';
import { useUsersByIds } from '@/hooks/useUsers';
import { PageSpinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState, Alert } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Table, type Column } from '@/components/ui/Table';
import { AssignUserForm } from '@/components/organizer/AssignUserForm';
import { CompetitionWorkspaceHeader } from '@/components/organizer/CompetitionWorkspace';
import { OrganizerJudgesPage } from './OrganizerJudgesPage';
import { PaymentMethod } from '@/api/types';
import type { CompetitionParticipant } from '@/api/types';

/**
 * Competitors of one competition. Joining needs no approval (own link, or added here by e-mail); an organizer can
 * remove a competitor, for example one who did not pay by the deadline.
 */
export function OrganizerParticipantsPage() {
  const { competitionId } = useCompetitionIdParam();
  const competition = useCompetition(competitionId);
  const assignments = useParticipantsForCompetition(competitionId);
  const assignParticipant = useAssignParticipant(competitionId);
  const removeParticipant = useRemoveParticipant(competitionId);
  const setPaid = useSetParticipantPaid(competitionId);
  const [copied, setCopied] = useState(false);
  const signupLink = `${window.location.origin}/razpis/${competition.data?.slug ?? competitionId}`;
  const setOpenForSignup = useSetOpenForSignup(competitionId);

  const { usersById } = useUsersByIds((assignments.data?.items ?? []).map((a) => a.userId));

  function describeUser(userId: string): string {
    const u = usersById.get(userId);
    if (!u) return userId;
    return u.displayName || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email;
  }

  const participants = assignments.data?.items ?? [];

  const columns: Column<CompetitionParticipant>[] = [
    { header: 'Tekmovalec', accessor: (a) => describeUser(a.userId) },
    { header: 'E-pošta', accessor: (a) => usersById.get(a.userId)?.email ?? '—' },
    { header: 'Dodano', accessor: (a) => new Date(a.invitedAt).toLocaleDateString() },
    {
      header: 'Plačilo',
      accessor: (a) => (
        <div className="flex flex-col gap-0.5">
          <select
            className="h-9 rounded-control border border-line-strong bg-surface-0 px-2 text-sm text-fg"
            aria-label={`Plačilo: ${describeUser(a.userId)}`}
            value={a.hasPaid ? String(a.paidVia ?? PaymentMethod.BankTransfer) : ''}
            disabled={setPaid.isPending && setPaid.variables?.participantId === a.id}
            onChange={(e) =>
              setPaid.mutate(
                e.target.value === ''
                  ? { participantId: a.id, hasPaid: false }
                  : { participantId: a.id, hasPaid: true, method: Number(e.target.value) as PaymentMethod },
              )
            }
          >
            <option value="">Ni plačano</option>
            <option value={PaymentMethod.BankTransfer}>Plačano: bančno nakazilo</option>
            <option value={PaymentMethod.Card}>Plačano: kartica</option>
          </select>
          {a.hasPaid && a.paidAt && <span className="text-xs text-fg-muted">{new Date(a.paidAt).toLocaleDateString()}</span>}
        </div>
      ),
    },
    {
      header: 'Dejanja',
      accessor: (a) => (
        <Button
          variant="danger"
          size="sm"
          isLoading={removeParticipant.isPending && removeParticipant.variables === a.id}
          onClick={() => {
            if (confirm(`Odstranite "${describeUser(a.userId)}" s tega tekmovanja?`)) {
              removeParticipant.mutate(a.id);
            }
          }}
        >
          Odstrani
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <CompetitionWorkspaceHeader
        competitionId={competitionId}
        title="Tekmovalci"
        description="Tekmovalci se prijavijo sami prek povezave na razpis in lahko takoj oddajajo. Lahko jih dodate tudi po e-pošti, odstranite in označite, kdo je že plačal."
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <div>
            <h2 className="font-semibold text-fg">Prijava tekmovalcev</h2>
            <p className="text-sm text-fg-muted">
              Ko so prijave odprte, se tekmovalci sami prijavijo prek povezave na razpis (brez potrjevanja).
            </p>
            <p className="mt-1 flex flex-wrap items-center gap-2 text-sm">
              <code className="rounded-control bg-surface-2 px-2 py-1 text-fg-secondary">{signupLink}</code>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void navigator.clipboard?.writeText(signupLink).then(() => setCopied(true));
                }}
              >
                {copied ? 'Kopirano' : 'Kopiraj povezavo'}
              </Button>
            </p>
          </div>
          <Button
            variant={competition.data?.isOpenForSignup ? 'danger' : 'secondary'}
            size="sm"
            isLoading={setOpenForSignup.isPending}
            disabled={!competition.data}
            onClick={() => setOpenForSignup.mutate(!competition.data?.isOpenForSignup)}
          >
            {competition.data?.isOpenForSignup ? 'Zapri prijave' : 'Odpri prijave'}
          </Button>
        </CardHeader>
      </Card>

      <Card>
        <CardBody>
          <AssignUserForm
            actionLabel="Dodeli tekmovalca"
            isPending={assignParticipant.isPending}
            error={assignParticipant.error}
            onAssign={(payload) => assignParticipant.mutateAsync(payload)}
          />
        </CardBody>
      </Card>

      {assignments.isLoading && <PageSpinner />}
      {assignments.isError && (
        <ErrorState message={getApiErrorMessage(assignments.error)} onRetry={() => assignments.refetch()} />
      )}
      {(removeParticipant.isError || setPaid.isError) && (
        <Alert variant="error">
          {getApiErrorMessage(removeParticipant.error ?? setPaid.error)}
        </Alert>
      )}

      {assignments.isSuccess && participants.length === 0 && (
        <EmptyState title="Še ni dodeljenih tekmovalcev" description="Zgoraj dodelite tekmovalca." />
      )}
      {assignments.isSuccess && participants.length > 0 && (
        <>
          <p className="text-sm text-fg-muted">
            Plačalo {participants.filter((a) => a.hasPaid).length} od {participants.length}
          </p>
          <Table columns={columns} rows={participants} getRowKey={(a) => a.id} />
        </>
      )}

      <div className="mt-4 border-t border-line pt-4">
        <h2 className="text-lg font-semibold text-fg">Sodniki</h2>
        <OrganizerJudgesPage embedded />
      </div>
    </div>
  );
}
